import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ImageIcon, Users, Handshake } from "lucide-react";
import { hasCompleteMobilePhone, maskCNPJ, maskCEP, maskPhone, maskCNAE, unmask } from "@/lib/masks";
import { LogoUploadField } from "@/components/LogoUploadField";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const ESTADOS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

const GRAUS_RISCO = [
  { value: "1", label: "Grau 1 - Risco Mínimo" },
  { value: "2", label: "Grau 2 - Risco Baixo" },
  { value: "3", label: "Grau 3 - Risco Médio" },
  { value: "4", label: "Grau 4 - Risco Alto" },
];

interface OverviewTabProps {
  formData: any;
  fieldErrors: Record<string, string>;
  onInputChange: (field: string, value: any) => void;
  channels?: Array<any>;
  user?: any;
  isBranch?: boolean;
  companyId?: number | null;
  readOnly?: boolean;
}

/**
 * V11: Sub-componente para seleção de representante
 * Busca representantes do canal e exibe dropdown condicional
 */
function RepresentativeSelector({
  formData,
  onInputChange,
  fieldErrors,
  readOnly,
  companyId,
  user,
}: {
  formData: any;
  onInputChange: (field: string, value: any) => void;
  fieldErrors: Record<string, string>;
  readOnly?: boolean;
  companyId?: number | null;
  user?: any;
}) {
  // Buscar representantes do canal
  // V15: Usar channelId do formData (admin pode selecionar canal) ou do usuário logado
  const channelId = formData?.channelId || user?.channelId;
  const normalizedChannelId = Number(channelId || 0);
  const hasValidChannelId = Number.isFinite(normalizedChannelId) && normalizedChannelId > 0;
  const { data: representatives, isLoading } = trpc.clients.listRepresentatives.useQuery(
    { channelId: normalizedChannelId },
    {
      // V15: Só buscar quando houver canal válido para evitar erro no mount da tela de novo cliente
      enabled: hasValidChannelId,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
    }
  );

  // Filtrar para não mostrar a própria empresa
  const availableReps = (representatives || []).filter(
    (rep: any) => rep.id !== companyId
  );

  if (!hasValidChannelId) {
    return (
      <Alert className="border-slate-200 bg-slate-50">
        <AlertCircle className="h-4 w-4 text-slate-500" />
        <AlertDescription className="text-slate-700">
          Selecione um canal para carregar os representantes disponíveis.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="p-3 border rounded-lg bg-gray-50">
        <p className="text-sm text-gray-500">Carregando representantes...</p>
      </div>
    );
  }

  if (availableReps.length === 0) {
    return (
      <Alert className="border-amber-200 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          Nenhum representante cadastrado neste canal. Cadastre uma empresa como representante primeiro.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <Label className="text-sm font-medium">Selecionar Representante *</Label>
      <Select
        value={formData.representativeCompanyId ? String(formData.representativeCompanyId) : ""}
        onValueChange={(value) => onInputChange("representativeCompanyId", parseInt(value))}
        disabled={readOnly}
      >
        <SelectTrigger className={fieldErrors.representativeCompanyId ? "border-red-500" : ""}>
          <SelectValue placeholder="Selecione o representante" />
        </SelectTrigger>
        <SelectContent>
          {availableReps.map((rep: any) => (
            <SelectItem key={rep.id} value={String(rep.id)}>
              {rep.nomeFantasia || rep.name} {rep.cnpj ? `(${rep.cnpj})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {fieldErrors.representativeCompanyId && (
        <p className="text-red-600 text-sm mt-1">{fieldErrors.representativeCompanyId}</p>
      )}
    </div>
  );
}

export default function OverviewTab({
  formData,
  fieldErrors,
  onInputChange,
  channels,
  user,
  isBranch,
  companyId,
  readOnly = false,
}: OverviewTabProps) {
  formData = formData ?? {};
  const isGlobalPlatformAdmin = ["admin", "salvia_operation_admin"].includes(user?.role || "");
  const phoneHasValue = Boolean((formData.phone || "").trim());
  const phoneError = fieldErrors.phone || (phoneHasValue && !hasCompleteMobilePhone(formData.phone) ? "Telefone deve ter DDD + 9 dígitos." : "");

  // V16: Todos os hooks DEVEM ser chamados antes de qualquer early return.
  // Mover hooks para o topo garante ordem estável entre renders e elimina
  // o erro "Expected static flag was missing" do React.

  // Mutations para logo
  const utils = trpc.useUtils();
  const uploadLogoMutation = trpc.clients.uploadLogo.useMutation({
    onSuccess: (data) => {
      toast.success("Logo enviada com sucesso!");
      onInputChange("logoUrl", data.logoUrl);
      if (companyId) {
        utils.clients.getById.invalidate({ id: companyId });
      }
    },
    onError: (error) => {
      const msg = error.message || "";
      const isJson = msg.startsWith("[") || msg.startsWith("{");
      toast.error(isJson ? "Não foi possível enviar o logo. Tente novamente." : (msg || "Erro ao enviar logo"));
    },
  });

  const removeLogoMutation = trpc.clients.removeLogo.useMutation({
    onSuccess: () => {
      toast.success("Logo removida com sucesso!");
      onInputChange("logoUrl", null);
      if (companyId) {
        utils.clients.getById.invalidate({ id: companyId });
      }
    },
    onError: (error) => {
      const msg = error.message || "";
      const isJson = msg.startsWith("[") || msg.startsWith("{");
      toast.error(isJson ? "Não foi possível remover o logo. Tente novamente." : (msg || "Erro ao remover logo"));
    },
  });

  const handleUploadLogo = async (file: File) => {
    if (!companyId) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      await uploadLogoMutation.mutateAsync({
        companyId,
        fileName: file.name,
        mimeType: file.type as "image/png" | "image/jpeg" | "image/webp",
        base64Data: base64,
        fileSize: file.size,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    if (!companyId) return;
    await removeLogoMutation.mutateAsync({ companyId });
  };

  // Early return DEPOIS de todos os hooks (regra dos hooks do React)
  if (!formData) return null;

  // Determinar se é filial (branch) — derivado de formData, seguro após o guard
  const isFilial = isBranch || !!formData?.matrizId;

  return (
    <div className="space-y-6">
      {/* Card 1: Dados da Empresa */}
      <Card>
        <CardHeader>
          <CardTitle>Dados da Empresa</CardTitle>
          <CardDescription>Informações básicas da empresa</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Nome Fantasia */}
          <div>
            <Label htmlFor="nomeFantasia" className="text-sm font-medium">
              Nome Fantasia *
            </Label>
            <Input
              id="nomeFantasia"
              placeholder="Ex: Empresa XYZ"
              value={formData.nomeFantasia || ""}
              onChange={(e) => onInputChange("nomeFantasia", e.target.value)}
              className={fieldErrors.nomeFantasia ? "border-red-500 ring-1 ring-red-500" : ""}
              disabled={readOnly}
            />
            {fieldErrors.nomeFantasia && (
              <p className="text-red-600 text-sm mt-1">{fieldErrors.nomeFantasia}</p>
            )}
          </div>

          {/* Razão Social */}
          <div>
            <Label htmlFor="razaoSocial" className="text-sm font-medium">
              Razão Social *
            </Label>
            <Input
              id="razaoSocial"
              placeholder="Ex: Empresa XYZ Ltda"
              value={formData.razaoSocial || ""}
              onChange={(e) => onInputChange("razaoSocial", e.target.value)}
              className={fieldErrors.razaoSocial ? "border-red-500 ring-1 ring-red-500" : ""}
              disabled={readOnly}
            />
            {fieldErrors.razaoSocial && (
              <p className="text-red-600 text-sm mt-1">{fieldErrors.razaoSocial}</p>
            )}
          </div>

          {/* CNPJ */}
          <div>
            <Label htmlFor="cnpj" className="text-sm font-medium">
              CNPJ *
            </Label>
            <Input
              id="cnpj"
              placeholder="XX.XXX.XXX/XXXX-XX"
              value={maskCNPJ(formData.cnpj || "")}
              onChange={(e) => onInputChange("cnpj", unmask(e.target.value))}
              className={fieldErrors.cnpj ? "border-red-500 ring-1 ring-red-500" : ""}
              disabled={readOnly}
            />
            {fieldErrors.cnpj && (
              <p className="text-red-600 text-sm mt-1">{fieldErrors.cnpj}</p>
            )}
          </div>

          {/* Canal - Obrigatório para os perfis administrativos globais da plataforma */}
          {isGlobalPlatformAdmin && (
            <div>
              <Label htmlFor="channelId" className="text-sm font-medium">
                Canal *
              </Label>
              {isFilial ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600">
                    <span className="text-sm">
                      {channels?.find((c: any) => c.id === formData.channelId)?.nomeFantasia || 
                       channels?.find((c: any) => c.id === formData.channelId)?.name || 
                       "Canal não encontrado"}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Canal herdado da Matriz (não editável em Filiais).</p>
                </>
              ) : (
                <Select
                  value={formData.channelId ? String(formData.channelId) : ""}
                  onValueChange={(value) => onInputChange("channelId", parseInt(value))}
                >
                  <SelectTrigger id="channelId" className={fieldErrors.channelId ? "border-red-500 ring-1 ring-red-500" : ""}>
                    <SelectValue placeholder="Selecione um canal" />
                  </SelectTrigger>
                  <SelectContent>
                    {channels?.map((channel: any) => (
                      <SelectItem key={channel.id} value={String(channel.id)}>
                        {channel.nomeFantasia || channel.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {fieldErrors.channelId && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.channelId}</p>
              )}
            </div>
          )}

          {/* CNAE, Atividade Principal, Grau de Risco */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="cnae" className="text-sm font-medium">
                CNAE *
              </Label>
              <Input
                id="cnae"
                placeholder="XXXX-X/XX"
                value={maskCNAE(formData.cnae || "")}
                onChange={(e) => onInputChange("cnae", unmask(e.target.value))}
                className={fieldErrors.cnae ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.cnae && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cnae}</p>
              )}
            </div>
            <div>
              <Label htmlFor="atividadePrincipal" className="text-sm font-medium">
                Atividade Principal *
              </Label>
              <Input
                id="atividadePrincipal"
                placeholder="Ex: Consultoria"
                value={formData.atividadePrincipal || ""}
                onChange={(e) => onInputChange("atividadePrincipal", e.target.value)}
                className={fieldErrors.atividadePrincipal ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.atividadePrincipal && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.atividadePrincipal}</p>
              )}
            </div>
            <div>
              <Label htmlFor="grauRisco" className="text-sm font-medium">
                Grau de Risco *
              </Label>
              <Select
                value={formData.grauRisco?.toString() || ""}
                onValueChange={(value) => onInputChange("grauRisco", value)}
                disabled={readOnly}
              >
                <SelectTrigger id="grauRisco" className={fieldErrors.grauRisco ? "border-red-500 ring-1 ring-red-500" : ""}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {GRAUS_RISCO.map((gr) => (
                    <SelectItem key={gr.value} value={gr.value}>
                      {gr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.grauRisco && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.grauRisco}</p>
              )}
            </div>
          </div>

          {/* Setor, Número de Funcionários */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sector" className="text-sm font-medium">
                Setor *
              </Label>
              <Input
                id="sector"
                placeholder="Ex: Tecnologia"
                value={formData.sector || ""}
                onChange={(e) => onInputChange("sector", e.target.value)}
                className={fieldErrors.sector ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.sector && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.sector}</p>
              )}
            </div>
            <div>
              <Label htmlFor="employeesCount" className="text-sm font-medium">
                Número de Funcionários *
              </Label>
              <Input
                id="employeesCount"
                type="number"
                placeholder="Ex: 50"
                value={formData.employeesCount || ""}
                onChange={(e) => onInputChange("employeesCount", parseInt(e.target.value) || 0)}
                className={fieldErrors.employeesCount ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.employeesCount && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.employeesCount}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card: Logo do Cliente */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Logo {isFilial ? "da Filial" : "do Cliente"}
          </CardTitle>
          <CardDescription>
            {companyId
              ? `Anexe a logo da empresa (PNG, JPG ou WebP • Máx. 2MB)`
              : `Salve ${isFilial ? "a filial" : "o cliente"} primeiro para habilitar o upload da logo.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {companyId ? (
            <LogoUploadField
              currentLogoUrl={formData?.logoUrl}
              onUpload={handleUploadLogo}
              onRemove={handleRemoveLogo}
              isUploading={uploadLogoMutation.isPending}
              isRemoving={removeLogoMutation.isPending}
              disabled={readOnly}
            />
          ) : (
            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                Você poderá anexar a logo após salvar {isFilial ? "a filial" : "o cliente"}.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Endereço Completo */}
      <Card>
        <CardHeader>
          <CardTitle>Endereço Completo</CardTitle>
          <CardDescription>Localização da empresa</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Logradouro */}
          <div>
            <Label htmlFor="logradouro" className="text-sm font-medium">
              Logradouro *
            </Label>
            <Input
              id="logradouro"
              placeholder="Ex: Rua das Flores"
              value={formData.logradouro || ""}
              onChange={(e) => onInputChange("logradouro", e.target.value)}
              className={fieldErrors.logradouro ? "border-red-500 ring-1 ring-red-500" : ""}
              disabled={readOnly}
            />
            {fieldErrors.logradouro && (
              <p className="text-red-600 text-sm mt-1">{fieldErrors.logradouro}</p>
            )}
          </div>

          {/* Número e Complemento */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="numero" className="text-sm font-medium">
                Número *
              </Label>
              <Input
                id="numero"
                placeholder="Ex: 123"
                value={formData.numero || ""}
                onChange={(e) => onInputChange("numero", e.target.value)}
                className={fieldErrors.numero ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.numero && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.numero}</p>
              )}
            </div>
            <div>
              <Label htmlFor="complemento" className="text-sm font-medium">
                Complemento
              </Label>
              <Input
                id="complemento"
                placeholder="Ex: Apto 101"
                value={formData.complemento || ""}
                onChange={(e) => onInputChange("complemento", e.target.value)}
                disabled={readOnly}
              />
            </div>
          </div>

          {/* Bairro e Cidade */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bairro" className="text-sm font-medium">
                Bairro *
              </Label>
              <Input
                id="bairro"
                placeholder="Ex: Centro"
                value={formData.bairro || ""}
                onChange={(e) => onInputChange("bairro", e.target.value)}
                className={fieldErrors.bairro ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.bairro && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.bairro}</p>
              )}
            </div>
            <div>
              <Label htmlFor="cidade" className="text-sm font-medium">
                Cidade *
              </Label>
              <Input
                id="cidade"
                placeholder="Ex: São Paulo"
                value={formData.cidade || ""}
                onChange={(e) => onInputChange("cidade", e.target.value)}
                className={fieldErrors.cidade ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.cidade && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cidade}</p>
              )}
            </div>
          </div>

          {/* Estado e CEP */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estado" className="text-sm font-medium">
                Estado *
              </Label>
              <Select
                value={formData.estado || ""}
                onValueChange={(value) => onInputChange("estado", value)}
                disabled={readOnly}
              >
                <SelectTrigger id="estado" className={fieldErrors.estado ? "border-red-500 ring-1 ring-red-500" : ""}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS_BRASIL.map((estado) => (
                    <SelectItem key={estado} value={estado}>
                      {estado}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.estado && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.estado}</p>
              )}
            </div>
            <div>
              <Label htmlFor="cep" className="text-sm font-medium">
                CEP *
              </Label>
              <Input
                id="cep"
                placeholder="XXXXX-XXX"
                value={maskCEP(formData.cep || "")}
                onChange={(e) => onInputChange("cep", unmask(e.target.value))}
                className={fieldErrors.cep ? "border-red-500 ring-1 ring-red-500" : ""}
                disabled={readOnly}
              />
              {fieldErrors.cep && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.cep}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Classificação */}
      <Card>
        <CardHeader>
          <CardTitle>Classificação</CardTitle>
          <CardDescription>Papéis e relacionamentos da empresa</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toggle Matriz */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">Esta empresa é Matriz?</Label>
              <p className="text-xs text-gray-600 mt-1">Permite cadastrar filiais</p>
            </div>
            <Switch
              checked={formData.isMatriz || false}
              onCheckedChange={(value) => onInputChange("isMatriz", value)}
              disabled={readOnly}
            />
          </div>

          {/* Toggle Controladora */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">Esta empresa é Controladora?</Label>
              <p className="text-xs text-gray-600 mt-1">Permite gerenciar grupo econômico</p>
            </div>
            <Switch
              checked={formData.isControladora || false}
              onCheckedChange={(value) => onInputChange("isControladora", value)}
              disabled={readOnly}
            />
          </div>

          {/* Informativo se Filial */}
          {formData.matrizId && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Esta empresa é filial. Não é possível alterar o status de Matriz.
              </AlertDescription>
            </Alert>
          )}

          {/* Informativo se Membro do Grupo */}
          {formData.controladoraId && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Esta empresa é membro de um grupo econômico. Gerenciado pela controladora.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Card V11: Representante e Tipo de Venda */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Handshake className="w-5 h-5" />
            Representante e Tipo de Venda
          </CardTitle>
          <CardDescription>Defina se esta empresa é um representante e o tipo de venda</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toggle Representante */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">Esta empresa é um Representante?</Label>
              <p className="text-xs text-gray-600 mt-1">Representantes podem ser vinculados a outros clientes</p>
            </div>
            <Switch
              checked={formData.isRepresentative || false}
              onCheckedChange={(value) => onInputChange("isRepresentative", value)}
              disabled={readOnly}
            />
          </div>

          {/* Tipo de Venda */}
          <div>
            <Label className="text-sm font-medium">Tipo de Venda</Label>
            <Select
              value={formData.salesType || "DIRECT"}
              onValueChange={(value) => {
                onInputChange("salesType", value);
                if (value === "DIRECT") {
                  onInputChange("representativeCompanyId", null);
                }
              }}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo de venda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DIRECT">Venda Direta</SelectItem>
                <SelectItem value="REPRESENTATIVE">Via Representante</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dropdown Representante (condicional) */}
          {formData.salesType === "REPRESENTATIVE" && (
            <RepresentativeSelector
              formData={formData}
              onInputChange={onInputChange}
              fieldErrors={fieldErrors}
              readOnly={readOnly}
              companyId={companyId}
              user={user}
            />
          )}

          {/* Badge informativo */}
          {formData.isRepresentative && (
            <Alert className="border-green-200 bg-green-50">
              <Users className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Esta empresa está marcada como <strong>Representante</strong>. Outros clientes poderão vinculá-la como representante de vendas.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Card 4: Contato */}
      <Card>
        <CardHeader>
          <CardTitle>Contato</CardTitle>
          <CardDescription>Informações de contato da empresa</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            <Input
              id="email"
              type="text"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="contato@empresa.com"
              value={formData.email || ""}
              onChange={(e) => onInputChange("email", e.target.value)}
              className={fieldErrors.email ? "border-red-500 ring-1 ring-red-500" : ""}
              disabled={readOnly}
            />
            {fieldErrors.email && (
              <p className="text-red-600 text-sm mt-1">{fieldErrors.email}</p>
            )}
          </div>

          {/* Telefone */}
          <div>
            <Label htmlFor="phone" className="text-sm font-medium">
              Telefone
            </Label>
            <Input
              id="phone"
              placeholder="(11) 99999-9999"
              value={maskPhone(formData.phone || "")}
              onChange={(e) => onInputChange("phone", unmask(e.target.value))}
              className={phoneError ? "border-red-500 ring-1 ring-red-500" : ""}
              disabled={readOnly}
            />
            {phoneError && (
              <p className="text-red-600 text-sm mt-1">{phoneError}</p>
            )}
          </div>

          {/* Responsável */}
          <div>
            <Label htmlFor="responsibleName" className="text-sm font-medium">
              Responsável
            </Label>
            <Input
              id="responsibleName"
              placeholder="Nome do responsável"
              value={formData.responsibleName || ""}
              onChange={(e) => onInputChange("responsibleName", e.target.value)}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
